import jwtDecode from "jwt-decode";
import * as uuid from "uuid";
import { NavigationGuard } from "vue-router";

import {
    AuthUser,
    BiometricLoginResult,
    ClientSettings,
    LoginResult,
    PushNotificationInfo,
    VerifyCodeResponse
} from "@/models";
import { EventBus, EventNames } from "./eventBus";
import _gateway, { Gateway, GatewayImpl } from "./gateway";

export interface Auth {
  readonly idnId: number;
  readonly clientId: number;
  readonly pushInfo?: PushNotificationInfo;
  readonly token?: string;
  readonly user?: AuthUser;
  readonly deviceGuid: string;

  login(email: string, password: string, trustedDeviceSecret: string | undefined): Promise<LoginResult>;
  loginWithBiometrics(email: string, secret: string): Promise<BiometricLoginResult>;
  loginWith2FA(verificationCode: string): Promise<VerifyCodeResponse>;
  logout(): Promise<void>;

  /**
   * Indicates if the user has entered their email and password but has not yet completed the 2FA process. If the
   * user's JWT has exipred, it will be removed and this will return false.
   * */
  isLoggedInTo2fa(): boolean;

  /**
   * Indicates if the user is fully logged in (i.e. completed 2FA). If the user's JWT has expired, it will be removed
   * and this will return false.
   */
  isLoggedIn(): boolean;

  setIdnId(idnId: number): void;
  setClientId(clientId: number): void;
  hasClientSelected(): boolean;
  getClientSettings(): Promise<ClientSettings | null>;
  setPushNotificationInfo(info: PushNotificationInfo): void;
}

export const ClientIdStorageKey = "Messenger_ClientId";
export const IdnIdStorageKey = "Messenger_IdnId";
export const TokenStorageKey = "Messenger_JWT";
export const DeviceGuidStorateKey = "Messenger_DeviceGuid";

export class AuthImpl implements Auth {
  private _idnId!: number;
  private _clientId!: number;
  private _clientSettingsPromise?: Promise<ClientSettings>;
  private _clientSettings?: ClientSettings;
  private _pushInfo?: PushNotificationInfo;
  private _token?: string;
  private _user?: AuthUser;
  private _deviceGuid: string;

  constructor(private gateway: Gateway, private storage: Storage) {
    // To prevent a circular reference, set the gateway's auth service property when initialized
    (gateway as GatewayImpl).auth = this;

    this._deviceGuid = this.loadOrGenerateDeviceGuid();

    const token = storage.getItem(TokenStorageKey);
    const clientId = parseInt(storage.getItem(ClientIdStorageKey) || "0");
    const idnId = parseInt(storage.getItem(IdnIdStorageKey) || "0");

    this.setToken(token || undefined);
    // If the token is expired, remove it
    if (this.token && this.isTokenExpired()) {
      this.logoutInternal();
    }

    if (!clientId || !idnId) {
      this.setClientId(0);
      this.setIdnId(0);
    } else {
      this.setClientId(clientId);
      this.setIdnId(idnId);
    }
  }

  get idnId() {
    return this._idnId;
  }
  get clientId() {
    return this._clientId;
  }
  get pushInfo() {
    return this._pushInfo;
  }
  get token() {
    return this._token;
  }
  get user() {
    return this._user;
  }
  get deviceGuid() {
    return this._deviceGuid;
  }

  private loadOrGenerateDeviceGuid() {
    let deviceGuid = localStorage.getItem(DeviceGuidStorateKey);

    if (!deviceGuid) {
      deviceGuid = uuid.v4();
      localStorage.setItem(DeviceGuidStorateKey, deviceGuid);
    }

    return deviceGuid;
  }

  setClientId(clientId: number) {
    // Seeded data is using negative numbers, so treat non-zero (negative or positive) as valid
    if (clientId !== 0) {
      this.storage.setItem(ClientIdStorageKey, clientId.toString());
    } else {
      clientId = 0;
      this.storage.removeItem(ClientIdStorageKey);
    }

    this._clientId = clientId;
    window.clientId = clientId;
  }

  setIdnId(idnId: number) {
    // Seeded data is using negative numbers, so treat non-zero (negative or positive) as valid
    if (idnId !== 0) {
      this.storage.setItem(IdnIdStorageKey, idnId.toString());
    } else {
      idnId = 0;
      this.storage.removeItem(IdnIdStorageKey);
    }

    this._idnId = idnId;
    window.idnId = idnId;
  }

  setToken(jwt?: string) {
    if (jwt) {
      this.storage.setItem(TokenStorageKey, jwt);
    } else {
      this.storage.removeItem(TokenStorageKey);
    }
    this._token = jwt;
    this._user = jwt ? this.parseToken(jwt) : undefined;
  }

  private isTokenExpired(): boolean {
    if (!this._token) return false;

    const token = jwtDecode<AuthToken>(this._token);
    return token.exp * 1000 < Date.now();
  }

  setPushNotificationInfo(pushInfo: PushNotificationInfo) {
    this._pushInfo = pushInfo;

    if (this.isLoggedIn() && pushInfo && pushInfo.deviceToken) {
      this.gateway.updatePushInfo(pushInfo);
    }
  }

  async login(email: string, password: string, trustedDeviceSecret: string | undefined): Promise<LoginResult> {
    const result = await this.gateway.login(email, password, trustedDeviceSecret);
    if (result?.isSuccess) {
      await this.loginUser(result.jwt);
    }

    return result;
  }

  async loginWithBiometrics(email: string, secret: string): Promise<BiometricLoginResult> {
    const result = await this.gateway.loginWithBiometrics(email, secret);
    if (result.isSuccess) {
      await this.loginUser(result.jwt);
    }

    return result;
  }

  async loginWith2FA(verificationCode: string) {
    const result = await this.gateway.loginWith2FA(verificationCode);
    if (result.isSuccess) {
      await this.loginUser(result.jwt);
    }

    return result;
  }

  private async loginUser(jwt: string) {
    this.setToken(jwt);

    if (this.isLoggedIn()) {
      EventBus.$emit(EventNames.LoggedIn);

      // If the user is fully logged in (past 2FA) and already has any patients they are subscribed to, make sure to
      // use the current device's push info so that notifications come to this device.
      //
      // FYI this needs to happen after calling the login APIs (not during) because they don't have access to the
      // patient's subscriptions (due to dynamic query filters and since there isn't a logged in user yet)
      if (this.pushInfo) {
        await this.gateway.updatePushInfo(this.pushInfo);
      }
    }

    // If the user doesn't have access to the selected client, clear the selected client.
    // This probably will only happen during testing (when switching between users)
    if (this.clientId && !this.user?.clientIds.some(c => c === this.clientId)) {
      this.setClientId(0);
    }
  }

  private parseToken(jwt: string): AuthUser {
    const data = jwtDecode<AuthToken>(jwt);
    const clientIds = data.AllowedClientIds.split(",")
      .map(c => parseInt(c))
      // If any values couldn't parse, ignore them
      .filter(c => !isNaN(c));

    return {
      id: parseInt(data.nameid),
      firstName: data.given_name,
      lastName: data.family_name,
      name: data.unique_name,
      email: data.email,
      clientIds: clientIds,
      twoFactorAuth: data.IsTwoFactorAuthUser === "true",
      phone: data.MobileNumber
    };
  }

  async logout() {
    // Only call the logout API if the user is fully logged in. If the user has been authenticated for 2FA, only delete
    // the token.
    if (this.isLoggedIn()) {
      try {
        await this.gateway.logout();
      } catch (error) {
        // Nothing to do here if this fails
      }
    }

    this.logoutInternal();
  }

  private logoutInternal() {
    this.setToken(undefined);

    // Broadcase the logged out event so that the native app knows that the user logged out (or was logged out)
    EventBus.$emit(EventNames.LoggedOut);
  }

  async getClientSettings(): Promise<ClientSettings | null> {
    // If the user changed clients since the settings were last loaded, clear the settings and load them again
    // Don't use _clientSettings?.id because this code should only run if the client settings were previously loaded
    if (this._clientSettings && this._clientSettings.id !== this._clientId) {
      this._clientSettingsPromise = undefined;
      this._clientSettings = undefined;
    }

    // If the client settings are already loaded, return them
    if (this._clientSettings) return this._clientSettings;

    // If the client settings have been fetched but not returned, return the pending promise so the API doesn't keep
    // getting called
    if (this._clientSettingsPromise) return this._clientSettingsPromise;

    // Otherwise, load the client settings
    try {
      this._clientSettingsPromise = this.gateway.getClientSettings(this._clientId);
      this._clientSettings = await this._clientSettingsPromise;

      return this._clientSettings;
    } finally {
      // Once the API has returned (even if it failed), clear the promise since it won't be need in future calls, since
      // either:
      // 1) the settings are now stored in _clientSettings
      // 2) OR the API call failed (unlikely) and we want the next call to try to load the settings again (instead of
      //    just returning the promise for the failed API call)
      this._clientSettingsPromise = undefined;
    }
  }

  isLoggedInTo2fa(): boolean {
    if (!this._token) return false;
    if (this.isTokenExpired()) {
      this.logoutInternal();
      return false;
    }

    return !!this.user?.twoFactorAuth;
  }

  isLoggedIn() {
    if (!this._token) return false;
    if (this.isTokenExpired()) {
      this.logoutInternal();
      return false;
    }

    return !this.user?.twoFactorAuth;
  }

  hasClientSelected() {
    // Seeded data is using negative numbers, so treat non-zero (negative or positive) as valid
    return this._clientId !== 0;
  }
}

interface AuthToken {
  nameid: string;
  given_name: string;
  family_name: string;
  unique_name: string;
  email: string;
  MobileNumber?: string;
  AllowedClientIds: string;
  IsTwoFactorAuthUser: string;
  /** The time the token expires as the number of seconds since the Unix Epoch (Jan 1st 1970 at midnight) in UTC */
  exp: number;
}

// Expose Auth as a singleton instance
const auth: Auth = new AuthImpl(_gateway, localStorage);
export default auth;

/**
 * This auth guard ensures that the user is not logged into 2FA or fully logged in. This would probably only ever be
 * used on the Sign In page.
 *
 * If the user is already logged in, redirect them to the home page.
 * If the user is logged into 2FA, redirect them to the 2FA page.
 */
export const NotLoggedInAuthGuard: NavigationGuard<Vue> = function(to, from, next): void {
  // If fully logged in, redirect the user to the redirectTo page (if exists) or the home page
  if (auth.isLoggedIn()) {
    next((to.query.redirectTo as string | null) || "/");
  }
  // If the user is only authenticated for 2FA, redirect them to the 2FA page, passing the redirectTo value
  else if (auth.isLoggedInTo2fa()) {
    next({ path: "/2fa", query: { redirectTo: to.query.redirectTo } });
  }
  // Otherwise, the user is not logged in and is allowed to continue to this page
  else {
    next();
  }
};

/**
 * This auth guard ensures that the user has only been authenticated for 2FA.
 *
 * If the user is not logged in, redirect them to the sign in page.
 * If the user is fully logged in, redirect them to the home page.
 */
export const TwoFactorAuthGuard: NavigationGuard<Vue> = function(to, from, next): void {
  // If fully logged in, redirect the user to the redirectTo page (if exists) or the home page
  if (auth.isLoggedIn()) {
    next((to.query.redirectTo as string | null) || "/");
  }
  // If the user is not logged in, redirect them to the sign in page, passing the redirectTo value
  else if (!auth.isLoggedInTo2fa()) {
    next({ path: "/sign-in", query: { redirectTo: to.query.redirectTo } });
  }
  // Otherwise, the user is authenticated for 2FA and is allowed to continue to this page
  else {
    next();
  }
};

export const AuthGuard: NavigationGuard<Vue> = function(to, from, next): void {
  // If the user is only authenticated for 2FA, redirect them to the 2FA page, include this page as a redirectTo query
  // parameter so the user can return to this page after fully logging in
  if (auth.isLoggedInTo2fa()) {
    next({ path: "/2fa", query: { redirectTo: to.fullPath } });
  }
  // If the user isn't logged in, redirect them to the sign in page, include this page as a redirectTo query parameter
  // so the user can return to this page after fully logging in
  else if (!auth.isLoggedIn()) {
    next({ path: "/sign-in", query: { redirectTo: to.fullPath } });
  }
  // Otherwise, the user is fully logged in with a client selected and can continue to this page
  else {
    next();
  }
};

/**
 * This auth guard ensures that the user is fully authenticated and has a client/hospital selected.
 *
 * This is useful for ensure the user isn't accessing any hospital specific pages (e.g. Select Patients, My Patients,
 * etc.) without selecting a hospital first
 */
export const AuthClientSelectedGuard: NavigationGuard<Vue> = function(to, from, next): void {
  // Call AuthGuard, using a custom next function that will stop if the auth guard fails (paramter is not undefined)
  AuthGuard(to, from, nextParameter => {
    // If the auth guard failed (nextParameter is populated), then send rediect the user
    if (nextParameter) {
      next(nextParameter);
    }
    // Otherwise, if the user doesn't have a client selected, redirect to the select hospital page
    else if (!auth.hasClientSelected()) {
      next("/select-hospital");
    }
    // Otherwise, continue to this page
    else {
      next();
    }
  });
};
